import React from "react";
import {
  Package,
  Image as ImageIcon,
  Info,
  X,
  Settings,
  Award,
  CheckCircle,
  Cog,
} from "lucide-react";
import { Button } from "../../components/AdminSharedComponents";
import TextField from "../../components/ui/Form/TextField";
import TextareaField from "../../components/ui/Form/TextareaField";
import PageFormHeader from "../../components/PageFormHeader";
import FileField from "../../components/ui/Form/FileField";

const ProductFormComponent = ({
  formData,
  setFormData,
  tempValues,
  setTempValues,
  validationErrors,
  editingItem,
  fileInputRef,
  handleChange,
  handleImageChange,
  handleImageRemove,
  handleSubmit,
  onCancel,
  addItem,
  startEditing,
  updateItem,
  cancelEditing,
  removeItem,
  handleKeyPress,
  loading,
  submitting,
  error,
  isEdit = false,
}) => {
  // Render dynamic list sections
  const renderDynamicList = (
    arrayName,
    tempValueKey,
    placeholder,
    extraValueKey = null,
    icon = null
  ) => {
    const isEditing = editingItem.type === arrayName;

    let actionButtonDisabled = false;
    if (arrayName === "features") {
      actionButtonDisabled = !tempValues.feature.trim();
    } else if (arrayName === "specifications") {
      actionButtonDisabled =
        !tempValues.specName.trim() || !tempValues.specValue.trim();
    } else if (arrayName === "certifications") {
      actionButtonDisabled = !tempValues.certification.trim();
    } else if (arrayName === "testingCapabilities") {
      actionButtonDisabled = !tempValues.testingCapability.trim();
    } else if (arrayName === "validationMethods") {
      actionButtonDisabled = !tempValues.validationMethod.trim();
    }

    const sectionTitle =
      arrayName.charAt(0).toUpperCase() +
      arrayName.slice(1).replace(/([A-Z])/g, " $1");

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-primary-dark flex items-center justify-between">
            <span className="flex items-center gap-2">
              {icon}
              {sectionTitle}
            </span>
            {formData[arrayName].length > 0 && (
              <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                {formData[arrayName].length} items
              </span>
            )}
          </h3>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Current Items */}
          {formData[arrayName].length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Current Items:
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData[arrayName].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border shadow-sm"
                  >
                    <span className="flex-grow text-sm pr-3">
                      {typeof item === "object"
                        ? `${item.name}: ${item.value}`
                        : item}
                    </span>
                    <div className="flex space-x-2 flex-shrink-0">
                      <Button
                        type="button"
                        onClick={() => startEditing(arrayName, index)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 rounded-md hover:bg-blue-50"
                        disabled={submitting}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        onClick={() => removeItem(arrayName, index)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 rounded-md hover:bg-red-50"
                        disabled={submitting}
                      >
                        ×
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add/Edit Form */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              {isEditing ? "Edit Item:" : "Add New Item:"}
            </h4>

            <div className="space-y-3">
              {/* Form Fields */}
              {extraValueKey ? (
                // Two fields for specifications (name and value)
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={tempValues[tempValueKey]}
                    onChange={(e) =>
                      setTempValues({
                        ...tempValues,
                        [tempValueKey]: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addItem(arrayName);
                      }
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={placeholder}
                    disabled={submitting}
                  />
                  <input
                    type="text"
                    value={tempValues[extraValueKey]}
                    onChange={(e) =>
                      setTempValues({
                        ...tempValues,
                        [extraValueKey]: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addItem(arrayName);
                      }
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Specification Value"
                    disabled={submitting}
                  />
                </div>
              ) : (
                // Single field for other arrays
                <input
                  type="text"
                  value={tempValues[tempValueKey]}
                  onChange={(e) =>
                    setTempValues({
                      ...tempValues,
                      [tempValueKey]: e.target.value,
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addItem(arrayName);
                    }
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={placeholder}
                  disabled={submitting}
                />
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                {isEditing ? (
                  <>
                    <Button
                      variant="primary"
                      type="button"
                      onClick={updateItem}
                      disabled={actionButtonDisabled || submitting}
                      className="flex-1"
                    >
                      Update Item
                    </Button>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={cancelEditing}
                      className="flex-1"
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => addItem(arrayName)}
                    disabled={actionButtonDisabled || submitting}
                    className="w-full"
                  >
                    Add {sectionTitle.slice(0, -1)}
                    {!actionButtonDisabled && (
                      <span className="ml-2 text-xs opacity-75 hidden sm:inline">
                        (or press Enter)
                      </span>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <PageFormHeader
        title={isEdit ? "Edit Product" : "Add New Product"}
        backLink="/admin/products"
        backText="Back to Products"
        submitText={isEdit ? "Save Changes" : "Create Product"}
        loadingText={isEdit ? "Saving..." : "Creating..."}
        submitIcon={Package}
        loading={submitting}
        formId="product-form"
      />

      {/* Main Content */}
      <div className="px-4 sm:px-6 py-6 sm:py-8 pb-24 sm:pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {Object.keys(validationErrors).length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-red-800">
                Please fix the following errors:
              </h3>
              <ul className="list-disc pl-5 mt-2">
                {Object.entries(validationErrors).map(([key, value]) => (
                  <li key={key}>{value}</li>
                ))}
              </ul>
            </div>
          )}

          <form id="product-form" onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Basic Information & Product Image */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                      <Package className="h-5 w-5" />
                      Basic Information
                    </h2>
                  </div>

                  <div className="p-4 sm:p-6">
                    <div className="space-y-4">
                      <TextField
                        label="Product Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Enter product title"
                        required
                        maxLength={100}
                        error={validationErrors.title}
                        showCharCount={true}
                        disabled={submitting}
                      />

                      <TextareaField
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Enter product description"
                        rows={4}
                        maxLength={500}
                        error={validationErrors.description}
                        showCharCount={true}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>

                {/* Product Image */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-dark">
                      <ImageIcon className="h-5 w-5" />
                      Product Image
                    </h2>
                  </div>

                  <div className="p-4 sm:p-6">
                    <FileField
                      label="Upload Product Image"
                      name="image"
                      value={formData.image ? [formData.image] : []}
                      onChange={(files) =>
                        setFormData((prev) => ({
                          ...prev,
                          image: files[0] || null,
                          imagePreview: files[0]
                            ? URL.createObjectURL(files[0])
                            : prev.imagePreview,
                        }))
                      }
                      onRemove={() =>
                        setFormData((prev) => ({
                          ...prev,
                          image: null,
                          imagePreview: null,
                          currentAttachments: [],
                        }))
                      }
                      currentAttachments={formData.currentAttachments}
                      pendingRemovals={[]}
                      onMarkForRemoval={(id) =>
                        setFormData((prev) => ({
                          ...prev,
                          currentAttachments: prev.currentAttachments.filter(
                            (att) => att.id !== id
                          ),
                        }))
                      }
                      onUndoRemoval={() => {}}
                      accept="image/*"
                      maxSize={5}
                      disabled={submitting}
                      required
                      variant="single"
                    />
                  </div>
                </div>
              </div>

              {/* Product Details Sections */}
              <div className="space-y-6">
                {/* Features */}
                {renderDynamicList(
                  "features",
                  "feature",
                  "Enter feature description",
                  null,
                  <Settings className="h-5 w-5" />
                )}

                {/* Specifications - with name and value fields */}
                {renderDynamicList(
                  "specifications",
                  "specName",
                  "Specification Name",
                  "specValue",
                  <Cog className="h-5 w-5" />
                )}

                {/* Certifications */}
                {renderDynamicList(
                  "certifications",
                  "certification",
                  "Enter certification name",
                  null,
                  <Award className="h-5 w-5" />
                )}

                {/* Testing Capabilities */}
                {renderDynamicList(
                  "testingCapabilities",
                  "testingCapability",
                  "Enter testing capability",
                  null,
                  <CheckCircle className="h-5 w-5" />
                )}

                {/* Validation Methods */}
                {renderDynamicList(
                  "validationMethods",
                  "validationMethod",
                  "Enter validation method",
                  null,
                  <CheckCircle className="h-5 w-5" />
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductFormComponent;
